import { FileDownload } from "@mui/icons-material";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import { Box, IconButton, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type ReactNode, type SyntheticEvent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { FilterActiveDot } from "../../../components/UIToolkit/Badges/FilterActiveDot";
import { type FlatSwitchOption, SystemFlatSwitch } from "../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import type { SystemAutoCompleteOption } from "../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemDropdown } from "../../../components/UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import type { DropdownItem } from "../../../components/UIToolkit/Inputs/SystemDropDown/types";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { RoleOrJobSelector } from "../../../components/UIToolkit/molecules/RoleOrJobSelector";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { INK, NEUTRAL, SURFACE } from "../../../theme/tokens";
import type { Nurse, NurseRole } from "../../../utils/models";
import { LONG_PTO_BUTTON_BOTTOM_RESERVE_PX } from "../LongPTOButton/consts";
import { ScheduleTemplateHeader } from "../ScheduleTemplateHeader";
import { usePersistedEmptyMeansAllFilter, usePersistedExclusionFilter } from "../usePersistedScheduleTemplateFilter";
import { CoverageDetailGrid } from "./CoverageDetailGrid";
import { type CoverageEditWiring, CoverageGrid } from "./CoverageGrid";
import { CoverageLegend } from "./CoverageLegend";
import { type CoverageRow, type CoverageSection, CoverageTargetMode, UNASSIGNED_JOB_ID } from "./types";

type Density = "summary" | "detail";

/**
 * Live Coverage data (Summary + Detail share it), supplied by the production
 * `MasterScheduleCoverage` mount.
 */
type LiveCoverage = {
	/** Non-null when not-ready (loading / error / empty); render it in place of the grid. */
	fallbackNode: ReactNode | null;
	sections: CoverageSection[];
	weeks: number;
	cycleStart: DateTime;
	/** When present, the Summary grid's Location cells open the location-config editor. */
	editWiring?: CoverageEditWiring;
	/** Coverage target mode the grid was computed with, its setter, and whether the dept has a
	    ratio ladder — drives the toggle pair (ladder: census/max; no ladder: Target/Required).
	    hasRatioLadder is undefined until the first response lands; the switch waits for it. */
	targetMode?: CoverageTargetMode;
	onTargetModeChange?: (mode: CoverageTargetMode) => void;
	hasRatioLadder?: boolean;
};

type PrototypeProps = {
	liveCoverage: LiveCoverage;
	/** Schedule Template tab switcher (Weekly / Consecutive / Coverage), rendered in the header. */
	trailing?: ReactNode;
};

/** Vertical hairline between toolbar zones — tokenized, self-stretching. */
const ToolbarDivider = () => <Box sx={{ width: "1px", alignSelf: "stretch", my: 0.25, backgroundColor: NEUTRAL[200] }} />;

/**
 * The Master Schedule Coverage view — the read-only, per-row × per-cycle-day coverage grid (Summary
 * and Detail densities). The People axis has moved to the Staffing area (see `StaffingPeopleView`), so
 * this component is Coverage-only.
 */
export const CoverageSummaryPrototype = ({ liveCoverage, trailing }: PrototypeProps) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const [density, setDensity] = useState<Density>("summary");

	// Staff search — the header control the Weekly and Consecutive tabs already carry. Selecting a
	// person narrows the grid to the rows they actually work, which is the "where does this person sit
	// in the template?" question. Only the selection lives here; the header owns the control and its
	// option list.
	const [selectedNurseToFilterBy, setSelectedNurseToFilterBy] = useState<SystemAutoCompleteOption<Nurse> | null>(null);
	const handleNurseFilterChange = useCallback((_: SyntheticEvent, value: SystemAutoCompleteOption<Nurse> | null) => setSelectedNurseToFilterBy(value), []);

	// Coverage row predicate. Job-mode matches on `jobId`; else role chips match ANY of the row's roles.
	// `rowsAreJobKeyed` = `hasFilterableJobs`, the same signal `RoleOrJobSelector` uses, so they can't diverge.
	const makeCoverageRowMatcher = useCallback((rowsAreJobKeyed: boolean, selectedRoles: (NurseRole | "all")[], selectedJobIds: string[]) => {
		if (rowsAreJobKeyed) {
			if (selectedJobIds.length === 0) return () => true;
			const jobIdSet = new Set(selectedJobIds);
			return (row: CoverageRow) => !!row.jobId && jobIdSet.has(row.jobId);
		}
		if (selectedRoles.length === 0 || selectedRoles.includes("all")) return () => true;
		const roleSet = new Set<string>(selectedRoles);
		return (row: CoverageRow) => row.roles.some((role) => roleSet.has(role));
	}, []);

	const densityOptions: FlatSwitchOption<Density>[] = [
		{ label: t("schedule.template.v3.density.summary"), value: "summary", "data-testid": "ms3-density-summary" },
		{ label: t("schedule.template.v3.density.detail"), value: "detail", "data-testid": "ms3-density-detail" },
	];
	const targetModeOptions: FlatSwitchOption<CoverageTargetMode>[] = liveCoverage.hasRatioLadder
		? [
				{ label: t("schedule.template.v3.targetMode.census"), value: CoverageTargetMode.Census, "data-testid": "ms3-target-census" },
				{ label: t("schedule.template.v3.targetMode.max"), value: CoverageTargetMode.Max, "data-testid": "ms3-target-max" },
			]
		: [
				{ label: t("schedule.template.v3.targetMode.target"), value: CoverageTargetMode.Max, "data-testid": "ms3-target-target" },
				{ label: t("schedule.template.v3.targetMode.required"), value: CoverageTargetMode.Required, "data-testid": "ms3-target-required" },
			];

	const isCoverageSummary = density === "summary";
	const isCoverageDetail = density === "detail";

	// Resolve which sections/weeks/cycle the Coverage quadrants render.
	const coverageSections = liveCoverage.sections;
	const coverageWeeks = liveCoverage.weeks;
	const coverageCycleStart = liveCoverage.cycleStart;
	const coverageFallback = liveCoverage.fallbackNode;

	// In a job-managed dept the backend keys each Coverage row on its job and sends `jobId`/`jobName`
	// directly — no role→job bridge. The job filter therefore offers exactly the jobs present in the
	// rows, and the identity column / header label switch to the job (see `makeCoverageRowMatcher`).
	const isJobDept = useMemo(() => coverageSections.some((s) => s.rows.some((r) => !!r.jobId)), [coverageSections]);
	// Exclude the "Unassigned" sentinel — it's a config-error row, not a real job to filter by.
	const coverageJobOptionIds = useMemo(
		() => Array.from(new Set(coverageSections.flatMap((s) => s.rows.map((r) => r.jobId).filter((id): id is string => !!id && id !== UNASSIGNED_JOB_ID)))),
		[coverageSections],
	);
	// Filter mode follows real selectable jobs, not `isJobDept` — a sentinel-only dept shows role chips.
	const hasFilterableJobs = coverageJobOptionIds.length > 0;

	// Filter options derive from the FULL (unfiltered) coverage data so the dropdowns
	// can always re-expand a narrowed selection.
	const coverageRoleOptions = useMemo(
		() => Array.from(new Set(coverageSections.flatMap((s) => s.rows.flatMap((r) => r.roles)))).sort() as NurseRole[],
		[coverageSections],
	);
	const coverageLocations = useMemo(() => Array.from(new Set(coverageSections.flatMap((s) => s.rows.map((r) => r.location)))).sort(), [coverageSections]);
	const locationOptions = useMemo<DropdownItem<string>[]>(() => coverageLocations.map((loc) => ({ value: loc, label: loc })), [coverageLocations]);
	const coverageShifts = useMemo(() => Array.from(new Set(coverageSections.flatMap((s) => s.rows.map((r) => r.shift)))).sort(), [coverageSections]);
	const shiftOptions = useMemo<DropdownItem<string>[]>(() => coverageShifts.map((shift) => ({ value: shift, label: shift })), [coverageShifts]);

	// Filter selections, remembered per user, department and tab. An empty selection means no constraint.
	// Role and job selections are held in parallel (like DayViewFilters) so toggling the JobManagement flag
	// preserves the inactive dimension.
	const [coverageRoleList, setCoverageRoleList] = usePersistedExclusionFilter("coverage", "roles", coverageRoleOptions);
	const coverageSelectedRoles = useMemo<(NurseRole | "all")[]>(
		() => (coverageRoleList.length === coverageRoleOptions.length ? ["all"] : coverageRoleList),
		[coverageRoleList, coverageRoleOptions.length],
	);
	const setCoverageSelectedRoles = useCallback(
		(selected: (NurseRole | "all")[]) => {
			const roles = selected.filter((role): role is NurseRole => role !== "all");
			setCoverageRoleList(selected.includes("all") || roles.length === 0 ? coverageRoleOptions : roles);
		},
		[setCoverageRoleList, coverageRoleOptions],
	);
	const [coverageSelectedJobIds, setCoverageSelectedJobIds] = usePersistedEmptyMeansAllFilter("coverage", "jobs", coverageJobOptionIds);
	const [coverageSelectedLocations, setCoverageSelectedLocations] = usePersistedEmptyMeansAllFilter("coverage", "locations", coverageLocations);
	const [coverageSelectedShifts, setCoverageSelectedShifts] = usePersistedEmptyMeansAllFilter("coverage", "time", coverageShifts);

	// Apply the Coverage filters: keep a row that matches ALL active dimensions (AND across dimensions,
	// OR within a dimension); drop sections that empty out. An empty location/shift selection = no
	// constraint on that dimension. Skip the work when a loading/error/empty fallback is showing (the
	// grid isn't rendered from these rows). The staff filter keeps a row where the selected person is
	// assigned on ANY day of the horizon — the same "rows this nurse appears in" rule the
	// Weekly/Consecutive grids use.
	const nurseFilterId = selectedNurseToFilterBy?.value.id ?? null;
	const filteredCoverageSections = useMemo(() => {
		if (coverageFallback) return coverageSections;
		const rowMatches = makeCoverageRowMatcher(hasFilterableJobs, coverageSelectedRoles, coverageSelectedJobIds);
		const locationSet = new Set(coverageSelectedLocations);
		const shiftSet = new Set(coverageSelectedShifts);
		const matchesNurse = (row: CoverageRow) =>
			nurseFilterId === null || row.days.some((day) => day.names.some((assignment) => assignment.nurseId === nurseFilterId));
		return coverageSections
			.map((section) => ({
				...section,
				rows: section.rows.filter(
					(row) =>
						rowMatches(row) &&
						matchesNurse(row) &&
						(locationSet.size === 0 || locationSet.has(row.location)) &&
						(shiftSet.size === 0 || shiftSet.has(row.shift)),
				),
			}))
			.filter((section) => section.rows.length > 0);
	}, [
		coverageFallback,
		coverageSections,
		makeCoverageRowMatcher,
		hasFilterableJobs,
		coverageSelectedRoles,
		coverageSelectedJobIds,
		coverageSelectedLocations,
		coverageSelectedShifts,
		nurseFilterId,
	]);

	const renderCoverageBody = useCallback((): ReactNode => {
		if (coverageFallback) return coverageFallback;
		if (filteredCoverageSections.length === 0) {
			// A staff search that matched no rows gets its own surface: "this person works nowhere in the
			// template" is a real answer, not a dead-end filter combo.
			return selectedNurseToFilterBy ? (
				<EmptyState
					dataTestId="ms3-coverage-staff-empty"
					icon={<PersonSearchIcon />}
					title={t("schedule.template.v3.coverage.staffEmpty.title")}
					hint={t("schedule.template.v3.coverage.staffEmpty.hint", { name: selectedNurseToFilterBy.label })}
				/>
			) : (
				<EmptyState
					dataTestId="ms3-coverage-filter-empty"
					icon={<FilterAltOffIcon />}
					title={t("schedule.template.v3.filter.empty.title")}
					hint={t("schedule.template.v3.filter.empty.hint")}
				/>
			);
		}
		if (isCoverageSummary) {
			return (
				<CoverageGrid
					sections={filteredCoverageSections}
					weeks={coverageWeeks}
					cycleStart={coverageCycleStart}
					displayMode="ratio"
					editWiring={liveCoverage.editWiring}
					isJobDept={isJobDept}
				/>
			);
		}
		return (
			<CoverageDetailGrid
				sections={filteredCoverageSections}
				weeks={coverageWeeks}
				cycleStart={coverageCycleStart}
				editWiring={liveCoverage.editWiring}
				isJobDept={isJobDept}
			/>
		);
	}, [
		coverageFallback,
		filteredCoverageSections,
		isCoverageSummary,
		coverageWeeks,
		coverageCycleStart,
		liveCoverage.editWiring,
		isJobDept,
		selectedNurseToFilterBy,
		t,
	]);

	return (
		<Box data-testid="ms3-root" sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", backgroundColor: NEUTRAL[50] }}>
			{/* Header — the shared band, identical on every tab. */}
			<ScheduleTemplateHeader
				dataTestId="ms3-header"
				staffFilter={{ value: selectedNurseToFilterBy, onChange: handleNurseFilterChange }}
				trailing={trailing}
				actions={
					<InHouseTooltip title={t("schedule.template.export")}>
						<IconButton disabled data-testid="ms3-export" aria-label={t("schedule.template.v3.export.ariaLabel")}>
							<FileDownload color="primary" />
						</IconButton>
					</InHouseTooltip>
				}
			/>

			{/* Toolbar — flat single row: [density] | [time · role · location] … [display · collapse] */}
			<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, px: 2, pt: 1.5, pb: 1 }}>
				<Box
					data-testid="ms3-toolbar"
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
						flexWrap: "wrap",
						rowGap: 1,
						px: 1.5,
						py: 1,
						border: `1px solid ${NEUTRAL[200]}`,
						borderRadius: 2,
						backgroundColor: SURFACE,
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<SystemFlatSwitch options={densityOptions} activeIndexOrValue={density} onSwitch={(o) => setDensity(o.value)} data-testid="ms3-density-switch" />
					</Box>

					<ToolbarDivider />

					{/* Filters — shift-time / role-or-job / location, all multi-select and derived from the live rows. */}
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", rowGap: 1 }} data-testid="ms3-filters">
						<FilterActiveDot active={coverageSelectedShifts.length > 0} dataTestId="ms3-time-filter-active-dot">
							<SystemDropdown
								multiple
								items={shiftOptions}
								selectedItems={coverageSelectedShifts}
								onChangeMultiple={setCoverageSelectedShifts}
								label={t("schedule.template.v3.filter.time")}
								allOption={t("schedule.template.v3.filter.allTimes")}
								formControlSx={{ minWidth: 150 }}
								data-testid="ms3-time-filter"
							/>
						</FilterActiveDot>
						<Box data-testid="ms3-role-filter">
							{/* Keyed by department and role list: the role chips read their selection once at mount, and the
							    roles arrive with the coverage data, so this lets a remembered role filter show once they do. */}
							<RoleOrJobSelector
								key={`${chosenDepartment.department_id}:${coverageRoleOptions.join("|")}`}
								selectedRoles={coverageSelectedRoles}
								onSelectedRolesChanged={setCoverageSelectedRoles}
								availableRoles={coverageRoleOptions}
								selectedJobIds={coverageSelectedJobIds}
								onSelectedJobsChanged={setCoverageSelectedJobIds}
								availableJobIds={coverageJobOptionIds}
								jobFilterActive={coverageSelectedJobIds.length > 0}
								label={t("schedule.template.v3.filter.job")}
								formControlSx={{ minWidth: 160 }}
							/>
						</Box>
						<FilterActiveDot active={coverageSelectedLocations.length > 0} dataTestId="ms3-location-filter-active-dot">
							<SystemDropdown
								multiple
								items={locationOptions}
								selectedItems={coverageSelectedLocations}
								onChangeMultiple={setCoverageSelectedLocations}
								label={t("schedule.template.v3.filter.location")}
								allOption={t("schedule.template.v3.filter.allLocations")}
								formControlSx={{ minWidth: 150 }}
								data-testid="ms3-location-filter"
							/>
						</FilterActiveDot>
					</Box>

					{/* Display mode + collapse-all, pushed right */}
					<Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
						{liveCoverage.targetMode && liveCoverage.hasRatioLadder !== undefined && (
							<InHouseTooltip title={t(liveCoverage.hasRatioLadder ? "schedule.template.v3.targetMode.tooltip" : "schedule.template.v3.targetMode.noLadder")}>
								<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
									<Typography sx={{ fontSize: 12, color: INK.MUTED, whiteSpace: "nowrap" }}>{t("schedule.template.v3.targetMode.caption")}</Typography>
									<SystemFlatSwitch
										options={targetModeOptions}
										activeIndexOrValue={liveCoverage.targetMode}
										onSwitch={(o) => liveCoverage.onTargetModeChange?.(o.value)}
										data-testid="ms3-target-mode-switch"
									/>
								</Box>
							</InHouseTooltip>
						)}
						<InHouseTooltip title={t("schedule.template.v3.collapseAll")}>
							<IconButton size="small" disabled data-testid="ms3-collapse-all" aria-label={t("schedule.template.v3.collapseAll.ariaLabel")}>
								<UnfoldLessIcon />
							</IconButton>
						</InHouseTooltip>
					</Box>
				</Box>

				{/* Legend — Coverage tiers (Summary) or charge/OT/PTO markers (Detail) */}
				<CoverageLegend isPeople={false} isCoverageDetail={isCoverageDetail} />
			</Box>

			{/* Body. Bottom inset is deliberately larger than the horizontal one: it reserves the footprint
			    of the `position: fixed` Long-PTO button (see LongPTOButton/consts.ts), which would otherwise
			    cover — and swallow clicks on — the grid's last row. Spelled out as `paddingBottom` because
			    the `pb` shorthand would multiply the px value by 8. */}
			<Box sx={{ flex: 1, minHeight: 0, px: 2, paddingBottom: `${LONG_PTO_BUTTON_BOTTOM_RESERVE_PX}px` }}>{renderCoverageBody()}</Box>
		</Box>
	);
};
